import {AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {NgbNavModule} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {Subscription} from 'rxjs';
import {BreadcrumbService} from '@cdss/services/breadcrumb.service';
import {BreadcrumbItem} from '@cdss/services/breadcrumb-item.value';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {ContractsService, isAlreadyNotified} from '@shared/api/services/v2/contracts.service';
import {
    Contract,
    isContractActive,
    isContractDeactivated,
    isContractDeleted
} from '@shared/api/models/query/contract.model';
import {ContractUpdate} from '@shared/api/models/command/contract.model';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {AuthService} from '@shared/services/security/auth.service';
import {CONTRACTS_LIST_ROUTE} from '@shared/services/router/contract-routes';
import {ContractFormComponent, ContractFormValue} from '../form/contract-form.component';
import {ContractAdminsTabComponent} from './tabs/contract-admins-tab.component';
import {ContractOrganisationsTabComponent} from './tabs/contract-organisations-tab.component';
import {ContractHistoryTabComponent} from './tabs/contract-history-tab.component';
import {ContractDeletePanelComponent} from './contract-delete-panel.component';

/** Error codes the API sends in the body of a 422 (backend ExceptionListener, UNIVAPLAT-3501). */
const DUPLICATE_CODE = 'contract_code_already_exists';
const READ_ONLY = 'contract_read_only';

export type ContractLoadError = 'denied' | 'not-found' | 'error';

/**
 * UNIVAPLAT-3517: contract detail page (route `cdss/admin/contracts/:id`, VIEW_CONTRACTS)
 * with the General details tab; the Contract admins and History tabs are placeholders filled
 * by UNIVAPLAT-3518 / 3519. The backend owns error texts. No contract data is logged.
 */
@Component({
    selector: 'cdss-contract-detail-page',
    standalone: true,
    imports: [
        RouterLink,
        NgbNavModule,
        SpinnerComponent,
        ContractFormComponent,
        ContractOrganisationsTabComponent,
        ContractAdminsTabComponent,
        ContractHistoryTabComponent,
        ContractDeletePanelComponent,
    ],
    templateUrl: './contract-detail.page.html',
    styleUrls: ['./contract-detail.page.scss']
})
export class ContractDetailPage implements OnInit, OnDestroy, AfterViewChecked {
    readonly listRoute = '/' + CONTRACTS_LIST_ROUTE;

    contract: Contract | null = null;
    loading = true;
    loadError: ContractLoadError | null = null;
    errorMessage = '';
    activeTab: 'general' | 'organisations' | 'admins' | 'history' = 'general';

    saving = false;
    codeError: string | null = null;

    private contractId: number | null = null;
    private routeSub?: Subscription;
    private focusMessage = false;

    @ViewChild('message') private messageEl?: ElementRef<HTMLElement>;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private breadcrumbService: BreadcrumbService,
        private contractsService: ContractsService,
        private authService: AuthService,
        private toastr: ToastrService,
    ) {
    }

    get isEditable(): boolean {
        return !!this.contract && isContractActive(this.contract);
    }

    get canEditCode(): boolean {
        return this.hasPermission(Permissions.EDIT_CONTRACT_CODE);
    }

    get canViewOrganisations(): boolean {
        return this.hasPermission(Permissions.VIEW_CONTRACT_ORGANISATIONS);
    }

    get canManageAdmins(): boolean {
        return this.hasPermission(Permissions.MANAGE_CONTRACT_ADMINS);
    }

    get canDelete(): boolean {
        return !!this.contract && !isContractDeleted(this.contract) && this.hasPermission(Permissions.DELETE_CONTRACT);
    }

    get canViewHistory(): boolean {
        return this.hasPermission(Permissions.VIEW_CONTRACT_HISTORY);
    }

    get statusLabel(): string | null {
        if (!this.contract) {
            return null;
        }
        if (isContractDeleted(this.contract)) {
            return 'Deleted';
        }
        return isContractDeactivated(this.contract) ? 'Deactivated' : null;
    }

    ngOnInit(): void {
        this.setBreadcrumb(null);
        this.routeSub = this.route.paramMap.subscribe(params => {
            const id = Number(params.get('id'));
            this.contractId = Number.isInteger(id) && id > 0 ? id : null;
            this.activeTab = 'general';
            void this.load(true);
        });
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe();
    }

    /** Moves focus to the denied / not-found message once it is rendered. */
    ngAfterViewChecked(): void {
        if (this.focusMessage && this.messageEl) {
            this.focusMessage = false;
            this.messageEl.nativeElement.focus();
        }
    }

    async load(showSpinner = false): Promise<void> {
        if (this.contractId === null) {
            this.showLoadError(404);
            return;
        }
        if (showSpinner) {
            this.loading = true;
            this.contract = null;
            this.loadError = null;
        }
        try {
            this.contract = await this.contractsService.get(this.contractId);
            this.loadError = null;
            this.setBreadcrumb(this.contract.title);
        } catch (error: any) {
            // Never keep stale data on screen after a failed load.
            this.showLoadError(error?.status);
        } finally {
            this.loading = false;
        }
    }

    async onSave(value: ContractFormValue): Promise<void> {
        if (this.saving || !this.contract || !this.isEditable) {
            return;
        }
        this.saving = true;
        this.codeError = null;
        const body: ContractUpdate = {
            title: value.title,
            serviceProvider: value.serviceProvider,
            signatureDate: value.signatureDate,
            kickoffDate: value.kickoffDate,
        };
        // Only a user with EDIT_CONTRACT_CODE sends the code; the API rejects it for contract admins.
        if (this.canEditCode) {
            body.code = value.code;
        }
        try {
            await this.contractsService.update(this.contract.id, body);
            this.toastr.success('Contract updated', 'Success');
            await this.load();
        } catch (error: any) {
            const message: string | undefined = error?.error?.error;
            if (this.isErrorCode(error, DUPLICATE_CODE)) {
                this.codeError = message || 'This contract code already exists.';
            } else {
                if (!isAlreadyNotified(error)) {
                    this.toastr.error(message || 'Something went wrong. Please try again.');
                }
                if (this.isErrorCode(error, READ_ONLY)) {
                    // The contract was deactivated or deleted meanwhile: refresh so the form turns read-only.
                    await this.load();
                }
            }
        } finally {
            this.saving = false;
        }
    }

    /** The contract was deleted: the detail page no longer applies, so go back to the list. */
    async onDeleted(): Promise<void> {
        await this.router.navigateByUrl(this.listRoute);
    }

    private showLoadError(status: number | undefined): void {
        this.contract = null;
        this.loadError = status === 403 ? 'denied' : (status === 404 ? 'not-found' : 'error');
        this.errorMessage = this.loadError === 'denied'
            ? 'You do not have access to this contract.'
            : (this.loadError === 'not-found'
                ? 'This contract does not exist.'
                : 'The contract could not be loaded. Try again later.');
        this.focusMessage = true;
        this.loading = false;
    }

    private isErrorCode(error: any, code: string): boolean {
        const body = error?.error;
        return error?.status === 422 && [body?.code, body?.error, body?.message, body?.detail]
            .some(field => typeof field === 'string' && field.includes(code));
    }

    private hasPermission(permission: Permissions): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[permission] ?? []).some(role => userRoles.includes(role));
    }

    private setBreadcrumb(title: string | null): void {
        const items: BreadcrumbItem[] = [{title: 'Contracts', url: this.listRoute}];
        if (title) {
            items.push({title, url: null as unknown as string});
        }
        this.breadcrumbService.set(items);
    }
}
